import { useState, useEffect, useRef } from 'react';
import { cn } from '@utils/cn';
import Icon from './Icon';

/**
 * SmartImage Component - Production-grade image display with error handling
 *
 * Features:
 * - Loading skeleton/blur animation
 * - Error handling with fallback placeholder
 * - Aspect ratio support (square, video, wide)
 * - Object-fit: cover to prevent stretching
 * - Smooth transitions
 *
 * Per PART 2: Frontend Visual Experience
 * Per SOLID Principles: Single Responsibility (image display only)
 */
export interface SmartImageProps {
  /**
   * Image source URL
   */
  src: string | null | undefined;

  /**
   * Alt text for accessibility
   */
  alt: string;

  /**
   * Fallback image URL (optional)
   * If provided, will be used instead of default placeholder
   */
  fallbackSrc?: string;

  /**
   * Aspect ratio preset
   * - "square": 1:1 (e.g., product images, logos)
   * - "video": 16:9 (e.g., banners, hero images)
   * - "wide": 21:9 (e.g., restaurant banners)
   * - Custom: Pass a number (e.g., 4/3 for 4:3 ratio)
   */
  aspectRatio?: 'square' | 'video' | 'wide' | number;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Object fit behavior
   * @default "cover"
   */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';

  /**
   * Whether to show loading skeleton
   * @default true
   */
  showLoading?: boolean;

  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode;
}

const SmartImage = ({
  src,
  alt,
  fallbackSrc,
  aspectRatio = 'square',
  className,
  objectFit = 'cover',
  showLoading = true,
  loadingComponent,
}: SmartImageProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(src || null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Calculate aspect ratio
  const getAspectRatio = (): string => {
    if (typeof aspectRatio === 'number') {
      return `${(1 / aspectRatio) * 100}%`;
    }
    switch (aspectRatio) {
      case 'square':
        return '100%'; // 1:1
      case 'video':
        return '56.25%'; // 16:9
      case 'wide':
        return '42.86%'; // 21:9
      default:
        return '100%';
    }
  };

  // Reset state when src changes
  useEffect(() => {
    if (src) {
      setImageSrc(src);
      setIsLoading(true);
      setHasError(false);
    } else {
      setImageSrc(null);
      setIsLoading(false);
      setHasError(true);
    }
  }, [src]);

  // Cached image may fire load before we mount or not fire onLoad; check complete after paint
  useEffect(() => {
    if (!imageSrc) return;
    const id = requestAnimationFrame(() => {
      const img = imgRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        setIsLoading(false);
        setHasError(false);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [imageSrc]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Handle image error
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);

    // Try fallback if provided
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setIsLoading(true);
      setHasError(false);
    } else {
      // No fallback or fallback also failed
      setImageSrc(null);
    }
  };

  // Render loading skeleton
  const renderLoading = () => {
    if (!showLoading) return null;

    if (loadingComponent) {
      return loadingComponent;
    }

    return (
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse',
          'flex items-center justify-center'
        )}
        style={{ paddingBottom: getAspectRatio() }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon name="image" size={32} className="text-gray-300" />
        </div>
      </div>
    );
  };

  // Render error placeholder
  const renderError = () => {
    return (
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200',
          'flex items-center justify-center'
        )}
        style={{ paddingBottom: getAspectRatio() }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <Icon name="image" size={32} className="text-gray-400" />
          <span className="text-xs text-gray-500 text-center px-4">
            {alt}
          </span>
        </div>
      </div>
    );
  };

  // If no src and no fallback, show error immediately
  if (!src && !fallbackSrc) {
    return (
      <div className={cn('relative overflow-hidden', className)}>
        {renderError()}
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Loading skeleton */}
      {isLoading && renderLoading()}

      {/* Error placeholder */}
      {hasError && !isLoading && renderError()}

      {/* Actual image */}
      {imageSrc && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            'absolute inset-0 w-full h-full transition-opacity duration-300',
            isLoading ? 'opacity-0' : 'opacity-100',
            hasError && 'hidden'
          )}
          style={{
            objectFit,
          }}
        />
      )}

      {/* Aspect ratio spacer */}
      <div style={{ paddingBottom: getAspectRatio() }} />
    </div>
  );
};

export default SmartImage;
